class JigsawPuzzle extends HTMLElement {
  constructor() {
    super();
    this.grid = 3;
    this.rFrac = 0.15;
    this.boardAspectRatio = 1590 / 1169;
    this.pieces = [];
    this.trayOrder = [];
    this.locked = 0;
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.imageUrl = this.dataset.imageUrl;
    this.imageWidth = parseInt(this.dataset.imageWidth, 10);
    this.imageHeight = parseInt(this.dataset.imageHeight, 10);

    this.board = this.querySelector('[data-jigsaw-board]');
    this.stage = this.board;
    this.layoutEl = this.closest('.jigsaw-puzzle__layout') || this.board;
    this.trayLeft = this.querySelector('[data-jigsaw-tray-left]');
    this.trayRight = this.querySelector('[data-jigsaw-tray-right]');
    this.trayBelow = this.querySelector('[data-jigsaw-tray-below]');
    this.boardBackdrop = this.querySelector('[data-jigsaw-board-backdrop]');
    this.solvedOverlay = this.querySelector('[data-jigsaw-solved]');
    this.resetButton = this.querySelector('[data-jigsaw-reset]');

    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.shuffle());
    }

    this.build();
    this.lastWidth = window.innerWidth;
    window.addEventListener('resize', this.debounce(this.onResize, 250));
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  onResize() {
    // Mobile browsers fire resize when the address bar collapses/expands on scroll,
    // which only changes viewport height. Rebuilding on that would wipe placed pieces,
    // so only react to genuine width changes (orientation change, window resize).
    const newWidth = window.innerWidth;
    if (newWidth === this.lastWidth) return;
    this.lastWidth = newWidth;

    const wasSolved = this.locked === this.grid * this.grid;
    this.build();
    if (wasSolved) this.showSolved(true);
  }

  build() {
    this.board.querySelectorAll('.jigsaw-piece').forEach((el) => el.remove());
    this.locked = 0;
    if (this.solvedOverlay) this.solvedOverlay.hidden = true;

    this.board.style.height = '';
    const stageRect = this.stage.getBoundingClientRect();
    const boardSize = Math.min(stageRect.width, 640);
    // Lock the build area to the build board's own aspect ratio rather than the
    // uploaded puzzle photo's — the piece images use preserveAspectRatio="slice",
    // so the photo crops to fill this shape instead of distorting to match it.
    this.imgDisplayWidth = Math.round(boardSize);
    this.imgDisplayHeight = Math.round(boardSize / this.boardAspectRatio);

    this.board.style.width = this.imgDisplayWidth + 'px';

    if (this.boardBackdrop) {
      // Slightly overscan to cover any sub-pixel rounding gap at the stage's edge.
      this.boardBackdrop.style.width = this.imgDisplayWidth + 2 + 'px';
      this.boardBackdrop.style.height = this.imgDisplayHeight + 2 + 'px';
    }

    this.cellW = this.imgDisplayWidth / this.grid;
    this.cellH = this.imgDisplayHeight / this.grid;
    // Padding is cross-wise: a left/right edge's tab bulges horizontally by cellH * rFrac,
    // so horizontal padding must be sized off cellH (and vertical padding off cellW).
    this.padX = this.cellH * this.rFrac;
    this.padY = this.cellW * this.rFrac;

    this.vBoundary = Array.from({ length: this.grid }, () =>
      Array.from({ length: this.grid - 1 }, () => (Math.random() < 0.5 ? 1 : -1))
    );
    this.hBoundary = Array.from({ length: this.grid - 1 }, () =>
      Array.from({ length: this.grid }, () => (Math.random() < 0.5 ? 1 : -1))
    );

    this.pieces = [];
    for (let r = 0; r < this.grid; r++) {
      for (let c = 0; c < this.grid; c++) {
        this.pieces.push(this.createPiece(r, c));
      }
    }

    this.scatter();
  }

  edgeType(side, r, c) {
    if (side === 'top') {
      if (r === 0) return 'flat';
      return this.hBoundary[r - 1][c] === 1 ? 'blank' : 'tab';
    }
    if (side === 'bottom') {
      if (r === this.grid - 1) return 'flat';
      return this.hBoundary[r][c] === 1 ? 'tab' : 'blank';
    }
    if (side === 'left') {
      if (c === 0) return 'flat';
      return this.vBoundary[r][c - 1] === 1 ? 'blank' : 'tab';
    }
    if (side === 'right') {
      if (c === this.grid - 1) return 'flat';
      return this.vBoundary[r][c] === 1 ? 'tab' : 'blank';
    }
  }

  piecePathData(r, c) {
    const x0 = c * this.cellW;
    const y0 = r * this.cellH;
    const corners = {
      tl: [x0, y0],
      tr: [x0 + this.cellW, y0],
      br: [x0 + this.cellW, y0 + this.cellH],
      bl: [x0, y0 + this.cellH],
    };
    const sides = [
      { from: corners.tl, to: corners.tr, type: this.edgeType('top', r, c) },
      { from: corners.tr, to: corners.br, type: this.edgeType('right', r, c) },
      { from: corners.br, to: corners.bl, type: this.edgeType('bottom', r, c) },
      { from: corners.bl, to: corners.tl, type: this.edgeType('left', r, c) },
    ];
    let d = `M${corners.tl[0]},${corners.tl[1]} `;
    for (const side of sides) {
      const [x1, y1] = side.from;
      const [x2, y2] = side.to;
      if (side.type === 'flat') {
        d += `L${x2},${y2} `;
        continue;
      }
      const L = Math.hypot(x2 - x1, y2 - y1);
      const ux = (x2 - x1) / L;
      const uy = (y2 - y1) / L;
      const p1 = [x1 + ux * L * 0.35, y1 + uy * L * 0.35];
      const p2 = [x1 + ux * L * 0.65, y1 + uy * L * 0.65];
      const rad = L * this.rFrac;
      const sweep = side.type === 'tab' ? 1 : 0;
      d += `L${p1[0]},${p1[1]} A${rad},${rad} 0 0 ${sweep} ${p2[0]},${p2[1]} L${x2},${y2} `;
    }
    d += 'Z';
    return { d, x0, y0 };
  }

  createPiece(r, c) {
    const { d, x0, y0 } = this.piecePathData(r, c);
    const svgW = this.cellW + this.padX * 2;
    const svgH = this.cellH + this.padY * 2;
    const svgX = x0 - this.padX;
    const svgY = y0 - this.padY;

    const svgns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgns, 'svg');
    svg.setAttribute('class', 'jigsaw-piece__svg');
    svg.setAttribute('viewBox', `${svgX} ${svgY} ${svgW} ${svgH}`);
    svg.setAttribute('width', svgW);
    svg.setAttribute('height', svgH);

    const clipId = `jigsaw-clip-${this.instanceId()}-${r}-${c}`;
    const defs = document.createElementNS(svgns, 'defs');
    const clipPath = document.createElementNS(svgns, 'clipPath');
    clipPath.setAttribute('id', clipId);
    clipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    const clipShape = document.createElementNS(svgns, 'path');
    clipShape.setAttribute('d', d);
    clipPath.appendChild(clipShape);
    defs.appendChild(clipPath);
    svg.appendChild(defs);

    const image = document.createElementNS(svgns, 'image');
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', this.imageUrl);
    image.setAttribute('href', this.imageUrl);
    image.setAttribute('x', 0);
    image.setAttribute('y', 0);
    image.setAttribute('width', this.imgDisplayWidth);
    image.setAttribute('height', this.imgDisplayHeight);
    image.setAttribute('clip-path', `url(#${clipId})`);
    image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.appendChild(image);

    const outline = document.createElementNS(svgns, 'path');
    outline.setAttribute('d', d);
    outline.setAttribute('fill', 'none');
    outline.setAttribute('stroke', 'rgba(36,16,24,0.15)');
    outline.setAttribute('stroke-width', '1.5');
    svg.appendChild(outline);

    const wrapper = document.createElement('div');
    wrapper.className = 'jigsaw-piece';
    wrapper.style.left = svgX + 'px';
    wrapper.style.top = svgY + 'px';
    wrapper.appendChild(svg);
    this.board.appendChild(wrapper);

    const piece = {
      el: wrapper,
      row: r,
      col: c,
      homeX: svgX,
      homeY: svgY,
      dx: 0,
      dy: 0,
      locked: false,
    };

    this.attachDrag(piece);
    return piece;
  }

  instanceId() {
    if (!this._iid) this._iid = Math.random().toString(36).slice(2, 8);
    return this._iid;
  }

  scatter() {
    const spacing = Math.max(10, ((this.cellW + this.cellH) / 2) * 0.12);
    const pieceBoxW = this.cellW + this.padX * 2;
    const pieceBoxH = this.cellH + this.padY * 2;
    this.spacing = spacing;
    this.pieceBoxW = pieceBoxW;
    this.pieceBoxH = pieceBoxH;

    // Below 750px there isn't reliable room for flanking columns without risking
    // horizontal overflow, so mobile always uses the below-image tray.
    const isMobile = window.innerWidth < 750;
    let sideColumns = 0;
    if (!isMobile) {
      const layoutRect = this.layoutEl.getBoundingClientRect();
      const sideBudget = (layoutRect.width - this.imgDisplayWidth) / 2 - spacing;
      sideColumns = Math.floor((sideBudget + spacing) / (pieceBoxW + spacing));
    }
    this.sideColumns = sideColumns;

    [this.trayLeft, this.trayRight, this.trayBelow].forEach((el) => {
      if (el) el.style.display = 'none';
    });

    const order = this.shuffleArray(this.pieces.map((_piece, i) => i)).map((i) => this.pieces[i]);
    this.trayOrder = order;

    if (sideColumns >= 1) {
      this.layoutMode = 'side';
      this.layoutSides(order);
    } else {
      this.layoutMode = 'below';
      this.layoutBelow(order);
    }

    this.locked = 0;
  }

  // Re-lays-out only the still-unlocked pieces, so the tray compacts (later pieces
  // move up/over) as pieces get placed instead of leaving gaps below the fold.
  compactTray() {
    const remaining = this.trayOrder.filter((piece) => !piece.locked);
    if (!remaining.length) return;
    remaining.forEach((piece) => piece.el.classList.add('is-snapping'));
    if (this.layoutMode === 'side') {
      this.layoutSides(remaining);
    } else {
      this.layoutBelow(remaining);
    }
    setTimeout(() => {
      remaining.forEach((piece) => piece.el.classList.remove('is-snapping'));
    }, 220);
  }

  layoutBelow(list) {
    const spacing = this.spacing;
    const pieceBoxW = this.pieceBoxW;
    const pieceBoxH = this.pieceBoxH;
    const fieldWidth = this.imgDisplayWidth;
    const trayTop = this.imgDisplayHeight + spacing * 2;
    const columns = Math.max(1, Math.min(list.length || 1, Math.floor((fieldWidth + spacing) / (pieceBoxW + spacing))));
    const rows = Math.max(1, Math.ceil(list.length / columns));
    const gridWidth = columns * pieceBoxW + (columns - 1) * spacing;
    const startX = Math.max(0, (fieldWidth - gridWidth) / 2);

    list.forEach((piece, slot) => {
      const col = slot % columns;
      const row = Math.floor(slot / columns);
      this.placePiece(piece, startX + col * (pieceBoxW + spacing), trayTop + row * (pieceBoxH + spacing));
    });

    const trayContentHeight = rows * pieceBoxH + (rows - 1) * spacing;
    this.board.style.height = trayTop + trayContentHeight + spacing * 2 + 'px';

    if (this.trayBelow) {
      this.trayBelow.style.display = 'block';
      this.trayBelow.style.left = '0px';
      this.trayBelow.style.top = trayTop - spacing + 'px';
      this.trayBelow.style.width = fieldWidth + 'px';
      this.trayBelow.style.height = trayContentHeight + spacing * 2 + 'px';
    }
  }

  layoutSides(list) {
    const spacing = this.spacing;
    const pieceBoxW = this.pieceBoxW;
    const pieceBoxH = this.pieceBoxH;
    const sideColumns = this.sideColumns;
    const half = Math.ceil(list.length / 2);
    const leftGroup = list.slice(0, half);
    const rightGroup = list.slice(half);
    const emptyGroupWidth = sideColumns * pieceBoxW + (sideColumns - 1) * spacing;

    const placeGroup = (group, side) => {
      if (!group.length) return { groupWidth: emptyGroupWidth, groupHeight: 0 };
      const rows = Math.ceil(group.length / sideColumns);
      const groupHeight = rows * pieceBoxH + (rows - 1) * spacing;
      const startY = Math.max(0, (this.imgDisplayHeight - groupHeight) / 2);
      const groupWidth = sideColumns * pieceBoxW + (sideColumns - 1) * spacing;

      group.forEach((piece, slot) => {
        const col = slot % sideColumns;
        const row = Math.floor(slot / sideColumns);
        const y = startY + row * (pieceBoxH + spacing);
        const x =
          side === 'left'
            ? -spacing - groupWidth + col * (pieceBoxW + spacing)
            : this.imgDisplayWidth + spacing + col * (pieceBoxW + spacing);
        this.placePiece(piece, x, y);
      });

      return { groupWidth, groupHeight, startY };
    };

    const left = placeGroup(leftGroup, 'left');
    const right = placeGroup(rightGroup, 'right');
    const fieldHeight = Math.max(this.imgDisplayHeight, left.groupHeight, right.groupHeight);

    this.board.style.height = fieldHeight + 'px';

    if (this.trayLeft) {
      this.trayLeft.style.display = leftGroup.length ? 'block' : 'none';
      this.trayLeft.style.left = -spacing - left.groupWidth + 'px';
      this.trayLeft.style.top = '0px';
      this.trayLeft.style.width = left.groupWidth + 'px';
      this.trayLeft.style.height = fieldHeight + 'px';
    }
    if (this.trayRight) {
      this.trayRight.style.display = rightGroup.length ? 'block' : 'none';
      this.trayRight.style.left = this.imgDisplayWidth + spacing + 'px';
      this.trayRight.style.top = '0px';
      this.trayRight.style.width = right.groupWidth + 'px';
      this.trayRight.style.height = fieldHeight + 'px';
    }
  }

  placePiece(piece, cellX, cellY) {
    piece.dx = cellX - piece.homeX;
    piece.dy = cellY - piece.homeY;
    piece.locked = false;
    piece.el.classList.remove('is-locked');
    piece.el.style.transform = `translate(${piece.dx}px, ${piece.dy}px)`;
    piece.el.style.zIndex = 1;
  }

  shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  attachDrag(piece) {
    const el = piece.el;
    let startX = 0;
    let startY = 0;
    let originDx = 0;
    let originDy = 0;
    let dragging = false;

    const pointerDown = (e) => {
      if (piece.locked) return;
      dragging = true;
      el.setPointerCapture(e.pointerId);
      el.classList.add('is-dragging');
      el.style.zIndex = 50;
      startX = e.clientX;
      startY = e.clientY;
      originDx = piece.dx;
      originDy = piece.dy;
    };

    const pointerMove = (e) => {
      if (!dragging) return;
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      piece.dx = originDx + deltaX;
      piece.dy = originDy + deltaY;
      el.style.transform = `translate(${piece.dx}px, ${piece.dy}px)`;
    };

    const pointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
      const threshold = Math.max(this.cellW, this.cellH) * 0.22;
      const dist = Math.hypot(piece.dx, piece.dy);
      if (dist < threshold) {
        piece.dx = 0;
        piece.dy = 0;
        piece.locked = true;
        el.classList.add('is-snapping', 'is-locked');
        el.style.transform = 'translate(0px, 0px)';
        el.style.zIndex = 2;
        setTimeout(() => el.classList.remove('is-snapping'), 220);
        this.locked++;
        if (this.locked === this.pieces.length) {
          this.dispatchEvent(new CustomEvent('puzzle_completed', { bubbles: true, composed: true }));
          this.showSolved();
        } else {
          this.compactTray();
        }
      } else {
        el.style.zIndex = 10;
      }
    };

    el.addEventListener('pointerdown', pointerDown);
    el.addEventListener('pointermove', pointerMove);
    el.addEventListener('pointerup', pointerUp);
    el.addEventListener('pointercancel', pointerUp);
  }

  showSolved(silent) {
    this.board.style.height = this.imgDisplayHeight + 'px';
    [this.trayLeft, this.trayRight, this.trayBelow].forEach((el) => {
      if (el) el.style.display = 'none';
    });
    if (this.solvedOverlay) this.solvedOverlay.hidden = false;
    if (!silent) {
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  shuffle() {
    this.build();
  }
}

customElements.define('jigsaw-puzzle', JigsawPuzzle);
